import {
    inject,
    Injectable,
    PLATFORM_ID,
    REQUEST,
    REQUEST_CONTEXT,
} from '@angular/core';
import { DefaultUrlSerializer, Router, UrlTree } from '@angular/router';

import { Response } from 'express';

import { Config } from '../config';
import { ContentService } from '../content';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { RequestContext } from '../utils/models';

interface RedirectData {
    'url-from': string;
    'url-to': string;
}

@Injectable({
    providedIn: 'root',
})
export class RedirectsService {
    private config: Config = inject(Config);
    private contentService: ContentService = inject(ContentService);

    siteRedirects!: RedirectData[];
    urlSerializer = new DefaultUrlSerializer();
    language: string;
    platformId = inject(PLATFORM_ID);
    httpClient = inject(REQUEST);
    router = inject(Router);
    context: RequestContext = inject(REQUEST_CONTEXT) as RequestContext;
    constructor() {
        this.language = this.config.language;
    }

    async load(): Promise<void> {
        if (this.siteRedirects?.length) {
            return;
        }

        this.siteRedirects = await this.contentService.getComponentP(
            'redirects',
            undefined,
            true,
        );
    }

    urlWithoutLang(url: string) {
        const langExpression = /\/([A-Za-z]{3})(?:\/|$)/;
        const regEx = new RegExp(langExpression);

        const match = regEx.exec(url);

        const languageGroup = match && match[1] ? match[1] : '';
        const lang = this.config.isValidLangForSbu(languageGroup)
            ? languageGroup
            : '';
        return url.replace(`/${lang}/`, '');
    }

    redirectData(
        currentUrl: string,
        redirectData?: RedirectData[],
    ): RedirectData | undefined {
        if (!currentUrl) {
            return;
        }
        const data = redirectData ? redirectData : this.siteRedirects;
        const redirect = data?.find((item: RedirectData) => {
            const urlWithoutLang = this.urlWithoutLang(currentUrl);
            const navigationUrl = urlWithoutLang;
            const navigationFrom =
                item['url-from'] === '/'
                    ? item['url-from']
                    : item['url-from']?.replace(/^\//, '');
            return navigationFrom === navigationUrl;
        });

        return redirect;
    }

    serverRedirect(response: Response | undefined, path: string) {
        if (isPlatformServer(this.platformId)) {
            const redirection = this.redirectData(path);
            if (redirection) {
                response?.redirect(redirection['url-to']);
                return false;
            }
        }
    }

    browserRedirect(path: string): UrlTree | boolean | undefined {
        if (isPlatformBrowser(this.platformId)) {
            const redirection = this.redirectData(path);

            if (!redirection) {
                return true;
            }

            return this.router.createUrlTree([redirection['url-to']]);
        }
    }
}
